import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    ArrowLeft,
    Loader2,
    DollarSign,
    Users,
    TrendingDown,
    TrendingUp,
    Repeat,
    Download,
    Mail,
    Activity,
    Plus,
    X,
    Send,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import MoneyTabs from "@/components/coach/MoneyTabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (cents, currency = "usd") =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase(), maximumFractionDigits: 0 })
        .format((cents || 0) / 100);

const fmtPct = (n) => `${(n ?? 0).toFixed(1)}%`;

export default function Revenue() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [sendingReport, setSendingReport] = useState(false);

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/", { replace: true }); return; }
        if (user.role !== "owner" && !user.permissions?.view_reports) {
            toast.error("You don't have access to revenue");
            navigate("/coach", { replace: true });
        }
    }, [loading, user, navigate]);

    const load = async () => {
        setBusy(true);
        try {
            const r = await axios.get(`${API}/coach/revenue/summary`, { withCredentials: true });
            setData(r.data);
        } catch (e) {
            toast.error("Couldn't load revenue", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };
    useEffect(() => { if (user?.role === "owner" || user?.permissions?.view_reports) load(); }, [user]);

    const downloadCSV = async () => {
        setDownloading(true);
        try {
            const r = await axios.get(`${API}/coach/revenue/invoices.csv`, {
                withCredentials: true,
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `maxsc-invoices-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("CSV downloaded");
        } catch (e) {
            toast.error("CSV failed", { description: e?.response?.data?.detail });
        } finally {
            setDownloading(false);
        }
    };

    const sendAccountant = async () => {
        if (!window.confirm(`Send revenue summary to ${(data?.accountant_emails || []).join(", ") || "configured accountants"}?`)) return;
        setSendingReport(true);
        try {
            const r = await axios.post(
                `${API}/coach/revenue/send-accountant-report`,
                {},
                { withCredentials: true },
            );
            const sent = r.data?.sent || [];
            toast.success(`Sent to ${sent.length} recipient${sent.length === 1 ? "" : "s"}`, {
                description: sent.join(", "),
            });
        } catch (e) {
            toast.error("Send failed", { description: e?.response?.data?.detail });
        } finally {
            setSendingReport(false);
        }
    };

    if (loading || busy || !data) {
        return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-brand-red" /></div>;
    }
    if (user?.role !== "owner" && !user?.permissions?.view_reports) return null;

    return (
        <div data-testid="coach-revenue" className="min-h-screen bg-black text-white">
            <div className="max-w-7xl mx-auto px-4 pt-28 pb-10">
                <MoneyTabs />
                <Link to="/coach" className="inline-flex items-center gap-1 text-zinc-200 hover:text-white mb-6">
                    <ArrowLeft className="w-4 h-4" /> Coach console
                </Link>

                <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                    <div>
                        <h1 className="font-display text-5xl md:text-6xl tracking-wider">REVENUE</h1>
                        <p className="text-sm text-zinc-300 mt-2">As of {new Date(data.as_of).toLocaleString()}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            data-testid="revenue-download-csv"
                            onClick={downloadCSV}
                            disabled={downloading}
                            className="btn-ghost-tactical"
                        >
                            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                            Invoices CSV
                        </button>
                        <button
                            type="button"
                            data-testid="revenue-send-accountant"
                            onClick={sendAccountant}
                            disabled={sendingReport || (data.accountant_emails || []).length === 0}
                            title={(data.accountant_emails || []).length === 0 ? "Set ACCOUNTANT_EMAILS in backend .env" : "Email summary + CSV to accountants"}
                            className="btn-tactical"
                        >
                            {sendingReport ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                            Email accountant
                        </button>
                    </div>
                </div>

                <div data-testid="gross-revenue-row" className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <Metric label={`Gross revenue · ${data.this_month_label || "this month"}`} value={fmt(data.gross_revenue_this_month_cents || 0)} icon={DollarSign} accent />
                    <Metric label={`Net collected · ${data.this_month_label || "this month"}`} value={fmt(data.net_collected_this_month_cents || 0)} icon={DollarSign} />
                    <Metric label="Transactions (this month)" value={data.transactions_this_month || 0} icon={Repeat} />
                    <Metric label={`Gross · ${data.last_month_label || "last month"}`} value={fmt(data.gross_revenue_last_month_cents || 0)} accentNavy />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <Metric label="MRR"        value={fmt(data.mrr_cents)}        icon={DollarSign} accent />
                    <Metric label="ARR"        value={fmt(data.arr_cents)}        icon={TrendingUp} />
                    <Metric label="Active subs" value={data.active_subs}          icon={Users} />
                    <Metric label="Monthly churn" value={fmtPct(data.monthly_churn_rate_pct)} icon={TrendingDown} muted={data.monthly_churn_rate_pct === 0} />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                    <Metric label="ARPU"       value={fmt(data.arpu_cents)} />
                    <Metric label="Estimated LTV" value={fmt(data.ltv_cents)} />
                    <Metric label="New (30d)"  value={data.new_last_30_days} accentNavy />
                    <Metric label="Returning (30d)" value={data.returning_last_30_days} icon={Repeat} />
                </div>

                <div className="grid lg:grid-cols-2 gap-6">
                    {/* By plan */}
                    <Card title="By plan">
                        {data.by_plan.length === 0 ? (
                            <Empty msg="No active subscriptions yet." />
                        ) : (
                            <table className="w-full text-sm">
                                <thead className="text-[10px] tracking-wider uppercase text-zinc-300">
                                    <tr>
                                        <th className="text-left py-2">Plan</th>
                                        <th className="text-right py-2">Subs</th>
                                        <th className="text-right py-2">MRR</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.by_plan.map((p) => (
                                        <tr key={p.plan_id} className="border-t border-zinc-800">
                                            <td className="py-2.5">{p.plan_name}</td>
                                            <td className="py-2.5 text-right">{p.active_subs}</td>
                                            <td className="py-2.5 text-right font-display tracking-wider">{fmt(p.mrr_cents)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </Card>

                    {/* Recent activity */}
                    <Card title="Recent subscription activity">
                        {data.recent_subscriptions.length === 0 ? (
                            <Empty msg="No subscription events yet." />
                        ) : (
                            <ul className="divide-y divide-zinc-800">
                                {data.recent_subscriptions.map((s) => (
                                    <li key={s.subscription_id} className="py-3 flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="text-sm truncate">{s.user_name || s.user_email || "—"}</div>
                                            <div className="text-xs text-zinc-300 truncate">{s.plan_name}</div>
                                        </div>
                                        <div className="text-right">
                                            <span className={`text-[10px] tracking-wider uppercase px-2 py-1 border ${
                                                s.status === "active" || s.status === "trialing" ? "border-brand-navy-600 text-brand-navy-200"
                                                : s.status === "canceled" ? "border-brand-red text-brand-red"
                                                : "border-amber-500 text-amber-400"
                                            }`}>{s.status}</span>
                                            <div className="text-[10px] text-zinc-400 mt-1">
                                                {s.updated_at ? new Date(s.updated_at).toLocaleDateString() : ""}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Card>
                </div>

                {(data.accountant_emails || []).length > 0 && (
                    <p className="text-[11px] text-zinc-400 mt-6 inline-flex items-center gap-2">
                        <Activity className="w-3 h-3" />
                        Auto-emailing on the 1st of each month → {data.accountant_emails.join(", ")}
                    </p>
                )}

                <AccountantReportsCard />
            </div>
        </div>
    );
}

function AccountantReportsCard() {
    const [emails, setEmails] = useState([]);
    const [effective, setEffective] = useState([]);
    const [newEmail, setNewEmail] = useState("");
    const [savingR, setSavingR] = useState(false);
    const [month, setMonth] = useState(() => {
        const d = new Date();
        d.setDate(1);
        d.setMonth(d.getMonth() - 1);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    });
    const [sendingMonth, setSendingMonth] = useState(false);
    const [missed, setMissed] = useState([]);
    const [sendingMissed, setSendingMissed] = useState(false);

    const loadR = async () => {
        try {
            const r = await axios.get(`${API}/coach/revenue/accountant-recipients`, { withCredentials: true });
            setEmails(r.data?.emails || []);
            setEffective(r.data?.effective || []);
        } catch (_e) { /* non-fatal */ }
    };
    const loadMissed = async () => {
        try {
            const r = await axios.get(`${API}/coach/revenue/missed-accountant-reports?months=6`, { withCredentials: true });
            setMissed(r.data?.missed || []);
        } catch (_e) { /* non-fatal */ }
    };
    useEffect(() => { loadR(); loadMissed(); }, []);

    const sendMissed = async () => {
        if (!missed.length) return;
        const labels = missed.map((m) => m.period_label).join(", ");
        if (!window.confirm(`Send ${missed.length} missed report${missed.length === 1 ? "" : "s"} (${labels}) to ${effective.join(", ") || "your recipients"}?`)) return;
        setSendingMissed(true);
        try {
            const r = await axios.post(`${API}/coach/revenue/send-missed-accountant-reports?months=6`, {}, { withCredentials: true });
            const sentMonths = r.data?.sent_months || [];
            const labelFor = (k) => (missed.find((m) => m.period_key === k)?.period_label) || k;
            if (sentMonths.length) toast.success(`Sent ${sentMonths.length} missed report${sentMonths.length === 1 ? "" : "s"}`, { description: sentMonths.map(labelFor).join(", ") });
            else toast.message("Nothing missing — all months already delivered");
            await loadMissed();
        } catch (e) {
            toast.error("Send failed", { description: e?.response?.data?.detail });
        } finally {
            setSendingMissed(false);
        }
    };

    const save = async (list) => {
        setSavingR(true);
        try {
            const r = await axios.put(`${API}/coach/revenue/accountant-recipients`, { emails: list }, { withCredentials: true });
            setEmails(r.data?.emails || []);
            await loadR();
            toast.success("Recipients saved");
        } catch (e) {
            toast.error("Couldn't save recipients", { description: e?.response?.data?.detail });
        } finally {
            setSavingR(false);
        }
    };
    const addEmail = () => {
        const e = newEmail.trim();
        if (!e || !e.includes("@")) { toast.error("Enter a valid email"); return; }
        if (emails.some((x) => x.toLowerCase() === e.toLowerCase())) { setNewEmail(""); return; }
        save([...emails, e]);
        setNewEmail("");
    };
    const removeEmail = (e) => save(emails.filter((x) => x !== e));

    const sendMonth = async () => {
        if (!month) return;
        if (!window.confirm(`Send the ${month} report to ${effective.join(", ") || "your recipients"}?`)) return;
        setSendingMonth(true);
        try {
            const r = await axios.post(`${API}/coach/revenue/send-accountant-report`, { period_key: month }, { withCredentials: true });
            const sent = r.data?.sent || [];
            const failed = r.data?.failed || [];
            if (sent.length) toast.success(`Sent ${month} report`, { description: sent.join(", ") });
            if (failed.length) toast.error(`Failed for ${failed.map((f) => f.email).join(", ")}`);
            if (!sent.length && !failed.length) toast.message("Nothing to send for that month");
        } catch (e) {
            toast.error("Send failed", { description: e?.response?.data?.detail });
        } finally {
            setSendingMonth(false);
        }
    };

    return (
        <div data-testid="accountant-reports-card" className="mt-8 border border-zinc-800 rounded-2xl bg-zinc-950/50 p-5">
            <h2 className="text-[11px] tracking-[0.25em] uppercase text-zinc-200 mb-1">Accountant reports</h2>
            <p className="text-[11px] text-zinc-400 mb-5 inline-flex items-center gap-2">
                <Activity className="w-3 h-3" /> Auto-sends on the 1st each month, and auto-retries any missed month for up to 3 months.
            </p>

            <div className="mb-5">
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-2">Recipients</div>
                <div className="flex flex-wrap gap-2 mb-2">
                    {emails.length === 0 && (
                        <span className="text-xs text-zinc-500">
                            No recipients saved{effective.length ? ` (using default: ${effective.join(", ")})` : ""}
                        </span>
                    )}
                    {emails.map((e) => (
                        <span key={e} data-testid={`accountant-recipient-${e}`} className="inline-flex items-center gap-1.5 text-xs bg-zinc-800 border border-zinc-700 rounded px-2 py-1">
                            {e}
                            <button type="button" data-testid={`accountant-recipient-remove-${e}`} onClick={() => removeEmail(e)} disabled={savingR} className="text-zinc-400 hover:text-red-400">
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
                <div className="flex gap-2 max-w-md">
                    <input
                        data-testid="accountant-recipient-input"
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && addEmail()}
                        placeholder="accountant@email.com"
                        className="flex-1 min-w-0 bg-black border border-zinc-800 px-3 py-2 text-sm text-white placeholder-zinc-600 rounded focus:border-brand-red focus:outline-none"
                    />
                    <button type="button" data-testid="accountant-recipient-add" onClick={addEmail} disabled={savingR} className="btn-ghost-tactical whitespace-nowrap">
                        <Plus className="w-4 h-4" /> Add
                    </button>
                </div>
            </div>

            <div className="border-t border-zinc-800 pt-4">
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-2">Missed months (last 6)</div>
                {missed.length === 0 ? (
                    <p data-testid="accountant-missed-none" className="text-[11px] text-zinc-500">All completed months have a delivery on record — nothing missing.</p>
                ) : (
                    <div className="flex flex-wrap gap-2 items-center">
                        <div className="flex flex-wrap gap-1.5" data-testid="accountant-missed-list">
                            {missed.map((m) => (
                                <span key={m.period_key} data-testid={`accountant-missed-${m.period_key}`} className="inline-flex items-center gap-1 text-xs bg-amber-950/40 border border-amber-800/60 text-amber-200 rounded px-2 py-1">
                                    {m.period_label}
                                </span>
                            ))}
                        </div>
                        <button type="button" data-testid="accountant-send-missed" onClick={sendMissed} disabled={sendingMissed} className="btn-tactical whitespace-nowrap">
                            {sendingMissed ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send all missed ({missed.length})
                        </button>
                    </div>
                )}
                <p className="text-[11px] text-zinc-500 mt-2">Flushes every month with no delivery on record to your recipients in one tap.</p>
            </div>

            <div className="border-t border-zinc-800 pt-4 mt-4">
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-2">Send a specific month</div>
                <div className="flex flex-wrap gap-2 items-center">
                    <input
                        data-testid="accountant-month-input"
                        type="month"
                        value={month}
                        onChange={(e) => setMonth(e.target.value)}
                        className="bg-black border border-zinc-800 px-3 py-2 text-sm text-white rounded focus:border-brand-red focus:outline-none [color-scheme:dark]"
                    />
                    <button type="button" data-testid="accountant-send-month" onClick={sendMonth} disabled={sendingMonth || !month} className="btn-tactical whitespace-nowrap">
                        {sendingMonth ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send this month
                    </button>
                </div>
                <p className="text-[11px] text-zinc-500 mt-2">Use this to resend July, August, or any past month right now.</p>
            </div>
        </div>
    );
}

const Metric = ({ label, value, icon: Icon, accent, accentNavy, muted }) => (
    <div className={`border rounded-2xl p-5 bg-zinc-950/50 ${
        accent ? "border-brand-red/40" : accentNavy ? "border-brand-navy-700" : "border-zinc-800"
    }`}>
        <div className="flex items-center gap-2 text-[11px] tracking-wider uppercase text-zinc-300 mb-2">
            {Icon && <Icon className="w-3.5 h-3.5" />}
            {label}
        </div>
        <div className={`font-display text-3xl md:text-4xl tracking-wider ${
            muted ? "text-zinc-400" : accent ? "text-brand-red" : "text-white"
        }`}>{value}</div>
    </div>
);

const Card = ({ title, children }) => (
    <div className="border border-zinc-800 rounded-2xl bg-zinc-950/50">
        <div className="px-5 py-4 border-b border-zinc-800">
            <h2 className="text-[11px] tracking-[0.25em] uppercase text-zinc-200">{title}</h2>
        </div>
        <div className="p-5">{children}</div>
    </div>
);

const Empty = ({ msg }) => <p className="text-sm text-zinc-300">{msg}</p>;
